import { positionChecker } from 'src/logic'
import { I18NText, Position, State } from 'src/types'
import { getBaseCell, getColorCell } from 'src/util/simulationsSpecification'

// 基本項目ロジク
export const getDxMBaseCells = (state: State, position: Position) => {
  const { isCatcher, isGlove } = positionChecker(position)
  const productNumber = state.baseModel.productNumber
  const baseModelStateObj = {
    label: productNumber,
    value: productNumber
  }
  return [
    getBaseCell('基本モデル', baseModelStateObj),
    getBaseCell('タンナー', state.tanner),
    isGlove && getBaseCell('バックスタイル', state.backStyle),
    getBaseCell('利き腕', state.dominantArm),
    !isCatcher && getBaseCell('ポケットスタイル', state.materialPack),
    getBaseCell('指カバー/指当て', state.fingerGuardType),
    isGlove && getBaseCell('ウェブパーツ', state.webParts),
    isGlove && getBaseCell('薬指リング', state.loopOfRingFinger, true), // 一時的に価格非表示にする2026/01/09
    isGlove && getBaseCell('ハミダシカット', state.hamidashiCut)
  ].filter(Boolean)
}

// ウェブ2ロジック
export const checkDxMWeb2 = (webPartsValue: string): boolean => {
  return false
  //   return ['w26', 'wt6'].includes(webPartsValue)
}

const variablePartsGenerator = (state: State, position: Position, t: I18NText) => {
  const isLeatherIntegratedIndex = state?.hamidashiCut?.value === 'hamidashiCut'
  switch (position) {
    case 'pitcher':
      return [
        getColorCell(t.THUMB1, state.thumbOut, 'thumbOut'),
        getColorCell(t.THUMB2, state.thumbWeb, 'thumbWeb'),
        getColorCell(t.INDEX3, state.indexWeb, 'indexWeb'),
        !isLeatherIntegratedIndex && getColorCell(t.INDEX4, state.indexMiddle, 'indexMiddle')
      ].filter(Boolean)
    case 'infielder':
      const isUBack = state.baseModel.isUBack
      const isNormal = !isUBack
      return [
        isNormal && getColorCell(t.THUMB1, state.thumbOut, 'thumbOut'),
        isNormal && getColorCell(t.THUMB2, state.thumbWeb, 'thumbWeb'),
        isNormal && getColorCell(`${t.INDEX}${isLeatherIntegratedIndex ? '3-1' : '3'}`, state.indexWeb, 'indexWeb'),
        isUBack && getColorCell(t.THUMB1, state.thumbOut, 'thumbOut'),
        isUBack && getColorCell(t.THUMB2_INDEX3, state.thumbWebAndIndexWeb, 'thumbWebAndIndexWeb'),
        !isLeatherIntegratedIndex && getColorCell(t.INDEX4, state.indexMiddle, 'indexMiddle')
      ].filter(Boolean)
    case 'outfielder':
      // ベルト一体型を考慮
      return [
        getColorCell(t.THUMB1, state.thumbOut, 'thumbOut'),
        getColorCell(t.THUMB2, state.thumbWeb, 'thumbWeb'),
        getColorCell(t.INDEX3, state.indexWeb, 'indexWeb'),
        !isLeatherIntegratedIndex && getColorCell(t.INDEX4, state.indexMiddle, 'indexMiddle')
      ].filter(Boolean)
    default:
      return []
  }
}
// グラブ / カラー項目ロジック
export const getDxMGloveColorCells = (state: State, position: Position, t: I18NText) => {
  const needFingerCoverOrPad = state.fingerGuardType?.value !== 'none'
  const needWeb2 = checkDxMWeb2(state?.webParts?.value)
  const isDirectEmbroidery = ['directEmbroidery', 'unselected'].includes(state.specifyLabel.value)
  const isLabel = ['label'].includes(state.specifyLabel.value)
  const isLoopOfRingFinger = state?.loopOfRingFinger?.value === 'loopOfRingFinger'
  return [
    getColorCell(t.CATCH_SURFACE, state.catchFace, 'catchFace'),
    getColorCell(t.WEB, state.web, 'web'),
    needWeb2 && getColorCell(t.WEB2, state.web2, 'web2'),
    getColorCell(t.THUMB_HOOK, state.thumbHook, 'thumbHook'),
    getColorCell(t.LITTLE_HOOK, state.littleHook, 'littleHook'),
    needFingerCoverOrPad && getColorCell(t.FINGER_GUARD, state.indexCover, 'indexCover'),
    getColorCell(t.LININGS, state.linings, 'linings'),
    ...variablePartsGenerator(state, position, t),
    getColorCell(t.MIDDLE5, state.middleIndex, 'middleIndex'),
    getColorCell(t.MIDDLE6, state.middleRing, 'middleRing'),
    getColorCell(t.RING7, state.ringMiddle, 'ringMiddle'),
    getColorCell(t.RING8, state.ringLittle, 'ringLittle'),
    getColorCell(t.LITTLE9, state.littleRing, 'littleRing'),
    getColorCell(t.LITTLE10, state.littleOut, 'littleOut'),
    getColorCell(t.BAND, state.listBelt, 'listBelt'),
    isLoopOfRingFinger && getColorCell(t.LOOP_OF_RING, state.loopOfRingFingerColor, 'loopOfRingFingerColor'),
    getColorCell(t.EDGE, state.edge, 'edge'),
    getColorCell(t.LACE, state.lace, 'lace'),
    getColorCell(t.STITCH, state.stitch, 'stitch'),
    getColorCell(t.HAMIDASHI, state.hamidashi, 'hamidashi'),
    getColorCell('ムートン', state.mouton, 'mouton'),
    isDirectEmbroidery && getColorCell('DxMロゴ1', state.dxmLabelCore, 'dxmLabelCore'),
    isDirectEmbroidery && getColorCell('DxMロゴ2', state.dxmLabel, 'dxmLabel'),
    isLabel && getColorCell('DxMロゴ', state.dxmLabel, 'dxmLabel')
  ].filter(Boolean)
}

// ファーストミット / カラー項目ロジック
export const getDxMFirstMittColorCells = (state: State, t: I18NText) => {
  const isLoopOfRingFinger = state?.loopOfRingFinger?.value === 'loopOfRingFinger'
  const needFingerCoverOrPad = state.fingerGuardType.value !== 'none'
  const isDirectEmbroidery = ['directEmbroidery', 'unselected'].includes(state.specifyLabel.value)
  const isLabel = ['label'].includes(state.specifyLabel.value)
  return [
    getColorCell(t.CATCH_SURFACE, state.catchFace, 'catchFace'),
    getColorCell(t.WEB, state.web, 'web'),
    getColorCell(t.THUMB_HOOK, state.thumbHook, 'thumbHook'),
    getColorCell(t.LITTLE_HOOK, state.littleHook, 'littleHook'),
    needFingerCoverOrPad && getColorCell(t.FINGER_GUARD, state.indexCover, 'indexCover'),
    getColorCell(t.LININGS, state.linings, 'linings'),
    getColorCell(t.THUMB, state.thumb, 'thumb'),
    getColorCell(t.UNDER_WEB, state.underWeb, 'underWeb'),
    getColorCell(t.BOOMERANG, state.boomerang, 'boomerang'),
    getColorCell(t.BAND, state.listBelt, 'listBelt'),
    getColorCell('薬指リング', state.loopOfRingFingerColor, 'loopOfRingFingerColor'),
    isLoopOfRingFinger && getColorCell(t.LOOP_OF_RING, state.loopOfRingFingerColor, 'loopOfRingFingerColor'), // ベルト必須
    getColorCell(t.LACE, state.lace, 'lace'),
    getColorCell(t.EDGE, state.edge, 'edge'),
    getColorCell(t.STITCH, state.stitch, 'stitch'),
    getColorCell('ムートン', state.mouton, 'mouton'),
    isDirectEmbroidery && getColorCell('DxMロゴ 1', state.dxmLabelCore, 'dxmLabelCore'),
    isDirectEmbroidery && getColorCell('DxMロゴ 2', state.dxmLabel, 'dxmLabel'),
    isLabel && getColorCell('DxMロゴ', state.dxmLabel, 'dxmLabel')
  ].filter(Boolean)
}

// キャッチャーミット / カラー項目ロジック
export const getDxMCatcherMittColorCells = (state: State, t: I18NText) => {
  const needFingerCoverOrPad = state.fingerGuardType.value !== 'none'
  const isDirectEmbroidery = ['directEmbroidery', 'unselected'].includes(state.specifyLabel.value)
  const isLabel = ['label'].includes(state.specifyLabel.value)
  return [
    getColorCell(t.CATCH_SURFACE, state.catchFace, 'catchFace'),
    getColorCell(t.WEB, state.web, 'web'),
    getColorCell(t.THUMB_HOOK, state.thumbHook, 'thumbHook'),
    getColorCell(t.LITTLE_HOOK, state.littleHook, 'littleHook'),
    needFingerCoverOrPad && getColorCell(t.FINGER_GUARD, state.indexCover, 'indexCover'),
    getColorCell(t.LININGS, state.linings, 'linings'),
    getColorCell(t.STAND, state.fingerStand, 'fingerStand'),
    getColorCell(t.EDGE, state.edge, 'edge'),
    getColorCell('親指', state.thumb, 'thumb'),
    getColorCell('人差指・中指1', state.indexMiddleRight, 'indexMiddleRight'),
    getColorCell('中指2・薬指', state.middleLeftRing, 'middleLeftRing'),
    getColorCell('小指', state.little, 'little'),
    getColorCell(t.BAND, state.listBelt, 'listBelt'),
    getColorCell(t.LACE, state.lace, 'lace'),
    getColorCell(t.STITCH, state.stitch, 'stitch'),
    getColorCell(t.HAMIDASHI, state.hamidashi, 'hamidashi'),
    getColorCell(t.TARGET, state.catchFaceTarget, 'catchFaceTarget'),
    getColorCell(t.MOUTON, state.mouton, 'mouton'), // ベルトバックのみ選択できる
    isDirectEmbroidery && getColorCell('DxMロゴ 1', state.dxmLabelCore, 'dxmLabelCore'),
    isDirectEmbroidery && getColorCell('DxMロゴ 2', state.dxmLabel, 'dxmLabel'),
    isLabel && getColorCell('DxMロゴ', state.dxmLabel, 'dxmLabel')
  ].filter(Boolean)
}
